import React, { useState } from "react";
import { useSelector } from "react-redux";
import { DataTable } from "@/components/ui/data-table";
import {
  PlusCircle,
  Tag,
  Edit,
  Trash2,
  MoreVertical,
  Search,
} from "lucide-react";
import apiClient from "../../api/apiClient";
import { API_BASE_URL } from "../../api/header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { ReactSelect } from "@/components/ui/react-select";
import { Spinner } from "@/components/ui/spinner";
import { PageContainer } from "@/components/layout/PageContainer";
import RecordNotFound from "@/core/components/RecordNotFound";

const ExpensesCategory = () => {
  const queryClient = useQueryClient();
  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  const [searchText, setSearchText] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [category, setCategory] = useState({
    id: "",
    name: "",
    description: "",
    color: "#1976D2",
    is_active: true,
  });

  // ==================== QUERIES ====================
  const { data: categories = [], isLoading: isCategoriesLoading } = useQuery({
    queryKey: ["expense-categories"],
    queryFn: async () => {
      const res = await apiClient.get(`${API_BASE_URL}/expense-categories`);
      return res.data.data || [];
    },
    enabled: hasPermission("expense category read"),
  });

  // ==================== MUTATIONS ====================
  const saveCategoryMutation = useMutation({
    mutationFn: async (payload) => {
      const id = payload.id;
      const url = id
        ? `${API_BASE_URL}/expense-categories/${id}`
        : `${API_BASE_URL}/expense-categories`;
      const method = id ? "put" : "post";

      // Ensure is_active is sent as 1 or 0 if backend expects it,
      // or boolean if backend handles it. Old code sent Number(is_active).
      const data = { ...payload, is_active: payload.is_active ? 1 : 0 };

      return apiClient({ method, url, data });
    },
    onSuccess: () => {
      toast.success(`Category ${editMode ? "updated" : "saved"} successfully!`);
      queryClient.invalidateQueries(["expense-categories"]);
      handleCloseModal();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to save category");
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: async (id) => {
      return apiClient.delete(`${API_BASE_URL}/expense-categories/${id}`);
    },
    onSuccess: () => {
      toast.success("Category deleted successfully!");
      queryClient.invalidateQueries(["expense-categories"]);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to delete category");
    },
  });

  const filteredData = categories.filter((entry) =>
    Object.keys(entry).some((key) =>
      String(entry[key] ?? "")
        .toLowerCase()
        .includes(searchText.toLowerCase()),
    ),
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCategory((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    setCategory({
      id: "",
      name: "",
      description: "",
      color: "#1976D2",
      is_active: true,
    });
    setEditMode(false);
    setIsModalOpen(true);
  };

  const handleEdit = (c) => {
    setCategory({
      ...c,
      is_active: Boolean(c.is_active),
    });
    setEditMode(true);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    deleteCategoryMutation.mutate(deleteId);
    setIsDeleteDialogOpen(false);
  };

  const handleSubmit = async () => {
    if (!category.name || String(category.name).trim() === "") {
      toast.error("Name is required");
      return;
    }
    if (category.name && category.name.length > 255) {
      toast.error("Name must be <= 255 characters");
      return;
    }
    saveCategoryMutation.mutate(category);
  };

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span
            className="w-3 h-3 rounded-full border border-gray-200"
            style={{ backgroundColor: row.original.color }}
          ></span>
          <span>{row.original.name || "-"}</span>
        </div>
      ),
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ getValue }) => getValue() || "-",
    },
    {
      accessorKey: "is_active",
      header: "Status",
      cell: ({ row }) => (
        <Badge variant={row.original.is_active ? "success" : "destructive"}>
          {row.original.is_active ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {hasPermission("expense category update") && (
              <DropdownMenuItem onClick={() => handleEdit(row.original)}>
                <Edit className="mr-2 h-4 w-4" /> Edit
              </DropdownMenuItem>
            )}
            {hasPermission("expense category delete") && (
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => handleDelete(row.original.id)}
              >
                <Trash2 className="mr-2 h-4 w-4" /> Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  if (!hasPermission("expense category read")) {
    return <RecordNotFound />;
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Expense Categories"
          subtitle="Manage expense categories"
        />
        <PageHeaderActions>
          {hasPermission("expense category create") && (
            <Button onClick={handleAdd}>
              <PlusCircle className="mr-2 h-4 w-4" /> Add Category
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <FilterCard>
        <FilterSearch
          placeholder="Search categories..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={filteredData}
          isLoading={isCategoriesLoading}
          enableRowSelection={true}
          footerInfo={
            <span className="font-medium text-foreground">
              Total Categories: {filteredData.length || 0}
            </span>
          }
        />
      </TableCard>

      {/* Add/Edit Category Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editMode ? "Edit Category" : "Add Category"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                name="name"
                value={category.name}
                onChange={handleInputChange}
                placeholder="Enter category name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="color">Color</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  id="color"
                  name="color"
                  value={category.color}
                  onChange={handleInputChange}
                  className="w-12 h-10 p-1"
                />
                <Input
                  type="text"
                  value={category.color}
                  onChange={handleInputChange}
                  name="color"
                  className="flex-1"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                name="description"
                value={category.description}
                onChange={handleInputChange}
                placeholder="Enter description"
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <ReactSelect
                isDisabled={!hasPermission("expense category toggle_active")}
                options={[
                  { value: true, label: "Active" },
                  { value: false, label: "Inactive" },
                ]}
                value={[
                  { value: true, label: "Active" },
                  { value: false, label: "Inactive" },
                ].find((opt) => opt.value === category.is_active)}
                onChange={(opt) =>
                  setCategory({
                    ...category,
                    is_active: opt ? opt.value : false,
                  })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saveCategoryMutation.isPending}
            >
              {saveCategoryMutation.isPending ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" /> Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              expense category and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
};

export default ExpensesCategory;
